// Rate Comparison Utility Functions
// Compares CEC timesheet rates with employee database rates

import { Employee } from '../types/employee';
import { Contractor } from '../types/contractor';
import { EmployeeRateComparison, TimesheetRateAnalysis } from '../types/timesheet';
import { calculateBurdenedRate, RateCalculationInputs, ContractorRateConfig } from './rateCalculations.ts';

/**
 * Get the appropriate database rate for an employee from a specific contractor
 */
export const getEmployeeRate = (
  employee: Employee, 
  contractor: Contractor, 
  isOvertime: boolean = false
): number => {
  console.log(`RATE COMPARISON: Getting rate for ${employee.firstName || employee.name} ${employee.lastName || ''}, contractor: ${contractor.companyName}, isOvertime: ${isOvertime}`);
  console.log(`RATE COMPARISON: Employee base data - wage: ${employee.hourlyWage}, fica: auto-calc, insurance: ${employee.insurance}, vacation: ${employee.vacation}, carFuel: ${employee.carFuel}, markup: ${employee.markup}`);
  
  const baseInputs: RateCalculationInputs = {
    hourlyWage: employee.hourlyWage || 0,
    insurance: employee.insurance || 0,
    vacation: employee.vacation || 0,
    carFuel: employee.carFuel || 0,
    markup: employee.markup || 0,
    customRateAdjustments: employee.customRateAdjustments
  };

  const contractorConfig: ContractorRateConfig = {
    markupType: contractor.billing.markupType,
    markupValue: contractor.billing.markupValue
  };

  console.log(`RATE COMPARISON: Contractor config - ${contractor.companyName}: ${contractorConfig.markupType} markup of ${contractorConfig.markupValue}`);
  
  const regularRate = calculateBurdenedRate(baseInputs, contractorConfig);
  const overtimeRate = regularRate * 1.5; // Standard overtime multiplier
  
  const selectedRate = isOvertime ? overtimeRate : regularRate;
  console.log(`RATE COMPARISON: Calculated rate: Regular $${regularRate.toFixed(2)}, OT $${overtimeRate.toFixed(2)}, Selected: $${selectedRate.toFixed(2)}`);
  
  return selectedRate;
};

/**
 * Compare a single employee's CEC rate against database rate
 */
export const compareEmployeeRate = (
  employee: Employee | null,
  employeeName: string,
  firstName: string,
  lastName: string,
  cecRate: number,
  cecEarningDesc: string,
  cecAmount: number,
  cecHours: number,
  contractor: Contractor
): EmployeeRateComparison => {
  const isOvertime = (cecEarningDesc || '').toLowerCase().includes('overtime') || 
                    (cecEarningDesc || '').toLowerCase().includes('ot');
  
  let databaseRegularRate = 0;
  let databaseOvertimeRate = 0;
  let rateMatch = false;
  let rateDifference = 0;
  let rateDifferencePercentage = 0;
  let recommendedAction: EmployeeRateComparison['recommendedAction'] = 'investigate';
  let actionReason = '';
  
  if (employee) {
    databaseRegularRate = getEmployeeRate(employee, contractor, false);
    databaseOvertimeRate = getEmployeeRate(employee, contractor, true);
    
    const expectedRate = isOvertime ? databaseOvertimeRate : databaseRegularRate;
    
    // Allow for small rounding differences (within 1 cent)
    const tolerance = 0.01;
    rateMatch = Math.abs(cecRate - expectedRate) <= tolerance;
    
    if (!rateMatch) {
      rateDifference = cecRate - expectedRate;
      rateDifferencePercentage = expectedRate > 0 ? (rateDifference / expectedRate) * 100 : 0;
      
      // Determine recommended action
      if (expectedRate === 0) {
        recommendedAction = 'update_employee_rate';
        actionReason = 'Employee has no rate set in database for this contractor';
      } else if (Math.abs(rateDifferencePercentage) > 10) {
        recommendedAction = 'investigate';
        actionReason = `Large rate difference: ${rateDifferencePercentage.toFixed(1)}% (${rateDifference > 0 ? '+' : ''}$${rateDifference.toFixed(2)})`;
      } else if (cecRate > expectedRate) {
        recommendedAction = 'update_employee_rate';
        actionReason = `CEC rate higher by $${rateDifference.toFixed(2)} - consider updating employee rate`;
      } else {
        recommendedAction = 'notify_cec';
        actionReason = `CEC rate lower by $${Math.abs(rateDifference).toFixed(2)} - notify CEC of correct rate`;
      }
    } else {
      recommendedAction = 'no_action';
      actionReason = 'Rates match within acceptable tolerance';
    }
  } else {
    recommendedAction = 'investigate';
    actionReason = 'Employee not found in database - check employee mapping';
  }
  
  return {
    employeeId: employee?.id,
    employeeName,
    firstName,
    lastName,
    cecProvidedRate: cecRate,
    cecEarningDesc,
    cecAmount,
    cecHours,
    databaseRegularRate,
    databaseOvertimeRate,
    rateMatch,
    rateDifference,
    rateDifferencePercentage,
    recommendedAction,
    actionReason
  };
};

/**
 * Analyze all employees across all jobs in imported timesheet data
 */
export const analyzeCompleteTimesheetRates = (
  allTimesheetData: any[],
  employees: Employee[],
  contractor: Contractor,
  analyzedBy: string,
  employeeMappings: {[key: string]: string} = {}
): TimesheetRateAnalysis => {
  console.log('RATE COMPARISON: Starting comprehensive rate analysis across all jobs');
  console.log('RATE COMPARISON: Total timesheet objects:', allTimesheetData.length);
  
  const employeeComparisons: EmployeeRateComparison[] = [];
  const seenEmployees = new Set<string>(); // Track employees we've already analyzed
  let totalRateDiscrepancyAmount = 0;
  
  // Function to find employee in database
  const findEmployee = (firstName: string, lastName: string): Employee | null => {
    const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
    console.log(`RATE COMPARISON: Finding employee for: "${fullName}"`);
    
    // Check if there's a mapping for this employee
    if (employeeMappings[fullName]) {
      const mappedEmployeeName = employeeMappings[fullName].toLowerCase();
      console.log(`RATE COMPARISON: Found mapping: "${fullName}" -> "${mappedEmployeeName}"`);
      const mappedEmployee = employees.find(emp => {
        const empFullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
        const empName = (emp.name || '').toLowerCase();
        return empFullName === mappedEmployeeName || empName === mappedEmployeeName;
      });
      if (mappedEmployee) {
        console.log(`RATE COMPARISON: Found mapped employee: ${mappedEmployee.firstName || mappedEmployee.name} ${mappedEmployee.lastName || ''}`);
        return mappedEmployee;
      }
    }
    
    // If no mapping, try direct match
    const directMatch = employees.find(emp => {
      const empFullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
      const empName = (emp.name || '').toLowerCase();
      return empFullName === fullName || empName === fullName;
    });
    
    if (directMatch) {
      console.log(`RATE COMPARISON: Found direct match: ${directMatch.firstName || directMatch.name} ${directMatch.lastName || ''}`);
    } else {
      console.log(`RATE COMPARISON: No match found for "${fullName}"`);
    }
    
    return directMatch || null;
  };

  // Process all timesheet data to find all unique employees with rate data
  allTimesheetData.forEach((timesheetData, tsIndex) => {
    console.log(`RATE COMPARISON: Processing timesheet ${tsIndex + 1}: ${timesheetData.jobName}`);
    
    timesheetData.employeeData?.forEach((empData: any, empIndex: number) => {
      const employeeKey = `${empData.firstName || ''} ${empData.lastName || ''}`.trim().toLowerCase();
      
      // Skip if we've already analyzed this employee
      if (seenEmployees.has(employeeKey)) {
        console.log(`RATE COMPARISON: Skipping already analyzed employee: ${employeeKey}`);
        return;
      }
      
      console.log(`RATE COMPARISON: Analyzing employee ${empIndex} in timesheet ${tsIndex}:`, empData.firstName, empData.lastName, 'cecRate:', empData.cecRate);
      
      // Only analyze if we have rate information
      if (empData.cecRate && empData.cecRate > 0) {
        console.log('RATE COMPARISON: Employee has rate data, proceeding with analysis');
        const employee = findEmployee(empData.firstName || '', empData.lastName || '');
        
        const comparison = compareEmployeeRate(
          employee,
          `${empData.firstName || ''} ${empData.lastName || ''}`.trim(),
          empData.firstName || '',
          empData.lastName || '',
          empData.cecRate || 0,
          empData.cecEarningDesc || 'Regular',
          empData.cecAmount || 0,
          empData.cecHours || 0,
          contractor
        );
        
        employeeComparisons.push(comparison);
        seenEmployees.add(employeeKey);
        
        if (!comparison.rateMatch && comparison.rateDifference !== 0) {
          totalRateDiscrepancyAmount += Math.abs(comparison.rateDifference * (empData.cecHours || 0));
        }
        
        console.log('RATE COMPARISON: Analysis completed for employee:', comparison);
      } else {
        console.log('RATE COMPARISON: Employee has no rate data, skipping');
      }
    });
  });
  
  // Calculate summary statistics
  const employeesWithDiscrepancies = employeeComparisons.filter(comp => !comp.rateMatch).length;
  const rateDifferences = employeeComparisons.map(comp => comp.rateDifference);
  const averageRateDifference = rateDifferences.length > 0 
    ? rateDifferences.reduce((sum, diff) => sum + diff, 0) / rateDifferences.length 
    : 0;
  const maxRateDifference = rateDifferences.length > 0 ? Math.max(...rateDifferences) : 0;
  const minRateDifference = rateDifferences.length > 0 ? Math.min(...rateDifferences) : 0;
  
  const analysis: TimesheetRateAnalysis = {
    jobName: `Multiple Jobs (${allTimesheetData.length} jobs)`,
    jobNumber: 'MULTIPLE',
    weekEnding: allTimesheetData[0]?.weekEnding || new Date(),
    contractorName: contractor.companyName,
    totalEmployees: employeeComparisons.length,
    employeesWithRateDiscrepancies: employeesWithDiscrepancies,
    totalRateDiscrepancyAmount,
    employeeComparisons,
    averageRateDifference,
    maxRateDifference,
    minRateDifference,
    analyzedAt: new Date(),
    analyzedBy
  };
  
  console.log('RATE COMPARISON: Complete analysis finished:', analysis);
  return analysis;
};

/**
 * Analyze an entire timesheet for rate discrepancies (single job)
 */
export const analyzeTimesheetRates = (
  timesheetData: any,
  employees: Employee[],
  contractor: Contractor,
  analyzedBy: string,
  employeeMappings: {[key: string]: string} = {}
): TimesheetRateAnalysis => {
  console.log('RATE COMPARISON: Starting rate analysis for:', timesheetData.jobName, 'with contractor:', contractor.companyName);
  console.log('RATE COMPARISON: Employee data count:', timesheetData.employeeData?.length);
  
  const employeeComparisons: EmployeeRateComparison[] = [];
  let totalRateDiscrepancyAmount = 0;
  
  // Function to find employee in database
  const findEmployee = (firstName: string, lastName: string): Employee | null => {
    const fullName = `${firstName} ${lastName}`.trim().toLowerCase();
    console.log(`RATE COMPARISON: Finding employee for: "${fullName}"`);
    
    // Check if there's a mapping for this employee
    if (employeeMappings[fullName]) {
      const mappedEmployeeName = employeeMappings[fullName].toLowerCase();
      console.log(`RATE COMPARISON: Found mapping: "${fullName}" -> "${mappedEmployeeName}"`);
      const mappedEmployee = employees.find(emp => {
        const empFullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
        const empName = (emp.name || '').toLowerCase();
        return empFullName === mappedEmployeeName || empName === mappedEmployeeName;
      });
      if (mappedEmployee) {
        console.log(`RATE COMPARISON: Found mapped employee: ${mappedEmployee.firstName || mappedEmployee.name} ${mappedEmployee.lastName || ''}`);
        return mappedEmployee;
      }
    }
    
    // If no mapping, try direct match
    const directMatch = employees.find(emp => {
      const empFullName = `${emp.firstName || ''} ${emp.lastName || ''}`.trim().toLowerCase();
      const empName = (emp.name || '').toLowerCase();
      return empFullName === fullName || empName === fullName;
    });
    
    if (directMatch) {
      console.log(`RATE COMPARISON: Found direct match: ${directMatch.firstName || directMatch.name} ${directMatch.lastName || ''}`);
    } else {
      console.log(`RATE COMPARISON: No match found for "${fullName}"`);
    }
    
    return directMatch || null;
  };
  
  // Analyze each employee in the timesheet
  timesheetData.employeeData?.forEach((empData: any, index: number) => {
    console.log(`RATE COMPARISON: Analyzing employee ${index}:`, empData.firstName, empData.lastName, 'cecRate:', empData.cecRate);
    
    // Only analyze if we have rate information
    if (empData.cecRate && empData.cecRate > 0) {
      console.log('RATE COMPARISON: Employee has rate data, proceeding with analysis');
      const employee = findEmployee(empData.firstName || '', empData.lastName || '');
      const employeeName = `${empData.firstName || ''} ${empData.lastName || ''}`.trim();
      
      const comparison = compareEmployeeRate(
        employee,
        employeeName,
        empData.firstName || '',
        empData.lastName || '',
        empData.cecRate,
        empData.cecEarningDesc || '',
        empData.cecAmount || 0,
        // Calculate total hours for this employee
        (empData.monday || 0) + (empData.tuesday || 0) + (empData.wednesday || 0) + 
        (empData.thursday || 0) + (empData.friday || 0) + (empData.saturday || 0) + (empData.sunday || 0),
        contractor
      );
      
      employeeComparisons.push(comparison);
      console.log('RATE COMPARISON: Employee comparison result:', comparison);
      
      // Add to total discrepancy if there's a difference
      if (!comparison.rateMatch && comparison.rateDifference) {
        totalRateDiscrepancyAmount += Math.abs(comparison.rateDifference) * comparison.cecHours;
      }
    } else {
      console.log('RATE COMPARISON: Employee has no rate data, skipping analysis');
    }
  });
  
  const employeesWithDiscrepancies = employeeComparisons.filter(comp => !comp.rateMatch).length;
  
  // Calculate summary statistics
  const rateDifferences = employeeComparisons
    .filter(comp => comp.rateDifference !== undefined)
    .map(comp => comp.rateDifference!);
  
  const averageRateDifference = rateDifferences.length > 0 
    ? rateDifferences.reduce((sum, diff) => sum + diff, 0) / rateDifferences.length 
    : 0;
  
  const maxRateDifference = rateDifferences.length > 0 ? Math.max(...rateDifferences) : 0;
  const minRateDifference = rateDifferences.length > 0 ? Math.min(...rateDifferences) : 0;
  
  return {
    jobName: timesheetData.jobName,
    jobNumber: timesheetData.jobNumber,
    weekEnding: timesheetData.weekEnding,
    contractorName: contractor.companyName,
    totalEmployees: timesheetData.employeeData?.length || 0,
    employeesWithRateDiscrepancies: employeesWithDiscrepancies,
    totalRateDiscrepancyAmount,
    employeeComparisons,
    averageRateDifference,
    maxRateDifference,
    minRateDifference,
    analyzedAt: new Date(),
    analyzedBy
  };
};

/**
 * Generate a summary report of rate analysis
 */
export const generateRateAnalysisSummary = (analysis: TimesheetRateAnalysis): string => {
  const { totalEmployees, employeesWithRateDiscrepancies, totalRateDiscrepancyAmount, contractorName } = analysis;
  
  if (employeesWithRateDiscrepancies === 0) {
    return `✅ All ${totalEmployees} employee rates match ${contractorName} database rates.`;
  }
  
  const discrepancyPercentage = (employeesWithRateDiscrepancies / totalEmployees * 100).toFixed(1);
  const potentialCostImpact = totalRateDiscrepancyAmount.toFixed(2);
  
  return `⚠️ ${employeesWithRateDiscrepancies} of ${totalEmployees} employees (${discrepancyPercentage}%) have rate discrepancies with ${contractorName}. Potential cost impact: $${potentialCostImpact}`;
};